import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  FileSpreadsheet, 
  Plus, 
  Trash2, 
  AlertTriangle, 
  CheckCircle, 
  Calculator, 
  FileText,
  TrendingUp, 
  ArrowUpRight, 
  Download, 
  DollarSign, 
  HelpCircle,
  RefreshCw,
  TrendingDown
} from "lucide-react";

interface SpreadsheetRow {
  id: string;
  date: string; // YYYY-MM_DD
  category: "Hosting" | "Ad" | "Domain" | "Service";
  income: number;
  expenses: number;
}

export function NexusTurnoverSpreadsheet() {
  const verifiedPreamble = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";
  
  // Set up mock data representing a realistic sequence over the last 15 months leading to crossings of the limit
  const baseDefaultData: SpreadsheetRow[] = [
    { id: "row-1", date: "2025-03-15", category: "Hosting", income: 4500, expenses: 1200 },
    { id: "row-2", date: "2025-04-10", category: "Service", income: 6200, expenses: 800 },
    { id: "row-3", date: "2025-05-18", category: "Ad", income: 8500, expenses: 4000 },
    { id: "row-4", date: "2025-06-22", category: "Service", income: 7200, expenses: 950 },
    { id: "row-5", date: "2025-07-05", category: "Domain", income: 3400, expenses: 1700 },
    { id: "row-6", date: "2025-08-30", category: "Service", income: 9800, expenses: 1100 },
    { id: "row-7", date: "2025-09-12", category: "Hosting", income: 5100, expenses: 1400 },
    { id: "row-8", date: "2025-10-25", category: "Ad", income: 11200, expenses: 5500 },
    { id: "row-9", date: "2025-11-04", category: "Service", income: 12500, expenses: 1600 },
    { id: "row-10", date: "2025-12-20", category: "Service", income: 8300, expenses: 1200 },
    { id: "row-11", date: "2026-01-15", category: "Hosting", income: 6800, expenses: 1500 },
    { id: "row-12", date: "2026-02-28", category: "Domain", income: 4100, expenses: 3100 },
    { id: "row-13", date: "2026-03-10", category: "Service", income: 9500, expenses: 1400 }, // Total here is creeping up
    { id: "row-14", date: "2026-04-12", category: "Ad", income: 13000, expenses: 6000 }, // Will cross the £90k rolling 12m limit here
    { id: "row-15", date: "2026-05-20", category: "Service", income: 14500, expenses: 2200 }, // Well over £90k rule
  ];

  const [rows, setRows] = useState<SpreadsheetRow[]>(() => {
    const cached = localStorage.getItem("nexorax_vat_turnover_spreadsheet");
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        console.error("Failed to restore spreadsheet state", e);
      }
    }
    return baseDefaultData;
  });

  const [activeCell, setActiveCell] = useState<{ rowId: string; col: string } | null>(null);
  const [newRow, setNewRow] = useState<Omit<SpreadsheetRow, "id">>({
    date: new Date().toISOString().substring(0, 10),
    category: "Service",
    income: 8500,
    expenses: 1200
  });

  useEffect(() => {
    localStorage.setItem("nexorax_vat_turnover_spreadsheet", JSON.stringify(rows));
  }, [rows]);

  // Helper function to check if date b is within a 12-month rolling window ending at date a (inclusive)
  // Rolling 12m includes the current month and the 11 preceding calendar months
  const isWithinRolling12Months = (referenceDateStr: string, targetDateStr: string): boolean => {
    const refDate = new Date(referenceDateStr);
    const tarDate = new Date(targetDateStr);
    
    if (isNaN(refDate.getTime()) || isNaN(tarDate.getTime())) return false;
    
    // Target date must be <= reference date
    if (tarDate > refDate) return false;
    
    // Calculate 12 months prior
    const dynamicLimit = new Date(refDate);
    dynamicLimit.setMonth(dynamicLimit.getMonth() - 12);
    
    return tarDate >= dynamicLimit;
  };

  // Sort rows chronologically for excel formula consistency
  const sortedRows = [...rows].sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  // Calculate the running 12-month total for each row
  const getRunningTotalForRow = (currentRowIndex: number): number => {
    const targetRow = sortedRows[currentRowIndex];
    if (!targetRow) return 0;
    
    return sortedRows.slice(0, currentRowIndex + 1).reduce((sum, item) => {
      if (isWithinRolling12Months(targetRow.date, item.date)) {
        return sum + item.income;
      }
      return sum;
    }, 0);
  };

  const handleCellChange = (rowId: string, field: keyof SpreadsheetRow, value: any) => {
    setRows(prev => prev.map(r => {
      if (r.id === rowId) {
        let normalizedVal = value;
        if (field === "income" || field === "expenses") {
          normalizedVal = Math.max(0, parseFloat(value) || 0);
        }
        return { ...r, [field]: normalizedVal };
      }
      return r;
    }));
  };

  const handleAddRow = () => {
    const id = `row-${Date.now()}`;
    setRows(prev => [...prev, { id, ...newRow }]);
    // Reset add row controls to logical current defaults
    setNewRow({
      date: new Date().toISOString().substring(0, 10),
      category: "Service",
      income: 5000,
      expenses: 1000
    });
  };

  const handleDeleteRow = (id: string) => {
    setRows(prev => prev.filter(r => r.id !== id));
  };

  const handleRestoreDefaults = () => {
    if (window.confirm("Verify: Re-populate ledger with NexoraX high-fidelity default rolling records?")) {
      setRows(baseDefaultData);
    }
  };

  // Global calculations
  const totalIncome = rows.reduce((sum, r) => sum + r.income, 0);
  const totalExpenses = rows.reduce((sum, r) => sum + r.expenses, 0);
  const currentNetMargin = totalIncome - totalExpenses;

  // Find the highest rolling 12m turnover currently reached
  let maxRollingTurnover = 0;
  let vatMandatoryRequired = false;
  let earliestBreachDate = "";

  sortedRows.forEach((r, idx) => {
    const rollingSum = getRunningTotalForRow(idx);
    if (rollingSum > maxRollingTurnover) {
      maxRollingTurnover = rollingSum;
    }
    if (rollingSum > 90000) {
      vatMandatoryRequired = true;
      if (!earliestBreachDate) {
        earliestBreachDate = r.date;
      }
    }
  });

  return (
    <div id="nexus-turnover-spreadsheet-block" className="w-full bg-[#050914] border border-white/5 rounded-2xl p-6 shadow-2xl space-y-6 text-left relative">
      <div className="absolute top-0 right-0 w-44 h-44 bg-cyan-500/5 blur-3xl rounded-full pointer-events-none"></div>

      {/* Header and Controller */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-cyan-500/10 rounded-xl">
              <FileSpreadsheet className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-100 uppercase tracking-widest font-mono flex items-center gap-2">
                NEXUS NEXORAX INCOME/EXPENSE SPREADSHEET <span className="text-[10px] text-cyan-400 bg-cyan-950/40 border border-cyan-500/20 px-1.5 py-0.5 rounded uppercase font-bold font-mono">B2B LEDGER</span>
              </h3>
              <p className="text-[9px] font-mono text-slate-400 mt-1 uppercase">
                Active Rolling 12-Month Turnover Audit • HMRC VAT Threshold Regulator (£90,000 threshold compliant).
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleRestoreDefaults}
            className="px-3 py-1.5 bg-slate-900 border border-slate-800 text-[10px] text-slate-400 hover:text-slate-100 uppercase tracking-wider font-mono rounded-lg transition-all flex items-center gap-1.5 cursor-pointer"
            title="Reset to default mock values depicting rolling VAT breach thresholds"
          >
            <RefreshCw size={10} />
            <span>Reset Baseline</span>
          </button>
          <span className="text-[10px] text-slate-500 uppercase font-bold bg-black/40 px-2.5 py-1.5 border border-white/5 rounded-lg font-mono">
            COUNT: {rows.length} ROWS
          </span>
        </div>
      </div>

      {/* VAT Compliance Banner Status Indicator */}
      <div className={`p-4 rounded-xl border ${vatMandatoryRequired ? "bg-rose-950/20 border-rose-500/30 text-rose-300" : "bg-emerald-950/10 border-emerald-500/20 text-emerald-300"} flex flex-col md:flex-row items-start md:items-center justify-between gap-4 transition-all duration-300`}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0">
            {vatMandatoryRequired ? (
              <AlertTriangle className="w-5 h-5 text-rose-500 animate-pulse" />
            ) : (
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            )}
          </div>
          <div className="space-y-1">
            <div className="text-[11px] font-mono font-bold tracking-wider uppercase flex items-center gap-1.5">
              <span>HMRC COMPLIANCE DIRECTION:</span>
              <span className={`px-2 py-0.5 rounded text-[8px] font-mono font-black ${vatMandatoryRequired ? "bg-rose-500 text-white animate-pulse" : "bg-emerald-500 text-slate-950"}`}>
                {vatMandatoryRequired ? "MANDATORY VAT REGISTRATION REQUIRED" : "COMPLIANT • BELOW THRESHOLD"}
              </span>
            </div>
            <p className="text-[9.5px] font-mono text-slate-400 leading-normal uppercase">
              {vatMandatoryRequired 
                ? `CRITICAL: Your rolling 12-month taxable turnover of £${maxRollingTurnover.toLocaleString()} crossed the £90,000 threshold on ${earliestBreachDate}. UK VAT Act 1994 specifies you must register with HMRC within 30 days.` 
                : `Your maximum rolling 12-month turnover reached £${maxRollingTurnover.toLocaleString()}, which is compliant. HMRC mandatory registration triggers automatically upon exceeding £90,000.`}
            </p>
          </div>
        </div>

        <div className="text-right shrink-0 bg-black/40 border border-white/5 p-2 px-3.5 rounded-lg">
          <span className="text-[8px] font-mono text-slate-500 block uppercase font-bold">Max 12m Rolling Peak</span>
          <span className={`text-sm font-mono font-black ${vatMandatoryRequired ? "text-rose-400" : "text-emerald-400"}`}>
            £{maxRollingTurnover.toLocaleString()}
          </span>
          <span className="text-[7.5px] font-mono text-slate-500 block uppercase font-semibold">Threshold: £90,000</span>
        </div>
      </div>

      {/* Spreadsheet Formula Explanation Bar */}
      <div className="bg-[#030612]/80 border border-slate-800/80 rounded-xl p-3.5 space-y-1 text-slate-300 font-mono text-[10px] uppercase">
        <div className="flex justify-between text-slate-500 text-[8.5px] font-bold pb-1 border-b border-white/5 mb-1">
          <span>SPREADSHEET ACTIVE FORMULA ARCHITECTURE</span>
          <span>CELL LINKING LOGIC</span>
        </div>
        <div className="flex flex-col md:flex-row gap-2 justify-between">
          <span>
            <strong className="text-cyan-400">Column C (Income):</strong> EXCL. VAT REVENUE INPUT • <strong className="text-purple-400">Column D (Expenses):</strong> NET COSTS OUTFLOW
          </span>
          <span className="text-amber-400 font-semibold" title="Excel Sum formula">
            Column E Formula: <code className="bg-black/60 px-1.5 py-0.5 rounded text-white border border-white/5">=SUMIFS(C:C, A:A, "&gt;=EDATE(A#, -12)", A:A, "&lt;=A#")</code>
          </span>
        </div>
      </div>

      {/* Main Grid View */}
      <div className="border border-slate-800 rounded-xl overflow-hidden bg-black/40 shadow-xl transition-all">
        <div className="overflow-x-auto">
          <table className="w-full text-left font-mono border-collapse select-text">
            {/* Table Headers */}
            <thead className="bg-[#0c1226]/80 text-[10px] text-slate-400 uppercase tracking-wider text-center border-b border-slate-800">
              <tr>
                <th className="w-10 bg-slate-900/50 border-r border-slate-800 py-2.5 font-bold text-slate-500 uppercase">#</th>
                <th className="px-4 py-2.5 border-r border-slate-800 font-black text-slate-300 text-left">
                  Date
                  <span className="block text-[8px] font-bold text-cyan-500">COLUMN A</span>
                </th>
                <th className="px-4 py-2.5 border-r border-slate-800 font-black text-slate-300 text-center">
                  Category (B)
                  <span className="block text-[8px] font-bold text-cyan-500">COLUMN B</span>
                </th>
                <th className="px-4 py-2.5 border-r border-slate-800 font-black text-slate-300 text-right">
                  Income (Excl. VAT)
                  <span className="block text-[8px] font-bold text-cyan-500">COLUMN C</span>
                </th>
                <th className="px-4 py-2.5 border-r border-slate-800 font-black text-slate-300 text-right">
                  Expenses (D)
                  <span className="block text-[8px] font-bold text-cyan-500">COLUMN D</span>
                </th>
                <th className="px-4 py-2.5 border-r border-slate-800 font-black text-slate-300 text-right bg-slate-900/10">
                  Running 12m Turnover
                  <span className="block text-[8px] font-bold text-amber-500">COLUMN E</span>
                </th>
                <th className="px-3 py-2.5 font-black text-slate-400 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/80 text-[11px] text-slate-300 leading-normal">
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-slate-500 font-mono italic">
                    Blank grid. Use the row initialization controls below to seed ledger elements.
                  </td>
                </tr>
              ) : (
                sortedRows.map((row, idx) => {
                  const runningTotal = getRunningTotalForRow(idx);
                  const isBreached = runningTotal > 90000;
                  const rowNum = idx + 1;

                  return (
                    <motion.tr 
                      key={row.id} 
                      layout
                      className={`hover:bg-slate-900/35 transition-colors border-l-4 ${
                        isBreached ? "border-l-rose-500/80 bg-rose-500/5" : "border-l-slate-800/20"
                      }`}
                    >
                      {/* Row index code header */}
                      <td className="bg-slate-900/40 text-center font-bold text-slate-500 border-r border-slate-800 text-[10px] select-none py-2.5">
                        {rowNum}
                      </td>

                      {/* Cell A: Date */}
                      <td className="px-4 py-2.5 border-r border-slate-800 whitespace-nowrap min-w-[130px]">
                        <input
                          type="date"
                          value={row.date}
                          onChange={(e) => handleCellChange(row.id, "date", e.target.value)}
                          className="bg-transparent border-0 text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:bg-black/50 px-1 py-0.5 rounded w-full font-mono text-[11px]"
                        />
                      </td>

                      {/* Cell B: Category */}
                      <td className="px-4 py-2.5 border-r border-slate-800 text-center min-w-[125px]">
                        <select
                          value={row.category}
                          onChange={(e) => handleCellChange(row.id, "category", e.target.value as any)}
                          className="bg-black/60 border border-slate-800 text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-500 text-[10px] font-mono rounded px-1.5 py-0.5 block mx-auto uppercase font-bold"
                        >
                          <option value="Hosting">Hosting</option>
                          <option value="Ad">Ad</option>
                          <option value="Domain">Domain</option>
                          <option value="Service">Service</option>
                        </select>
                      </td>

                      {/* Cell C: Income */}
                      <td className="px-4 py-2.5 border-r border-slate-800 text-right min-w-[140px]">
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-slate-500 text-[10px]">£</span>
                          <input
                            type="number"
                            value={row.income}
                            onChange={(e) => handleCellChange(row.id, "income", e.target.value)}
                            onFocus={() => setActiveCell({ rowId: row.id, col: "C" })}
                            onBlur={() => setActiveCell(null)}
                            className="bg-transparent border-0 text-right font-bold text-emerald-400 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:bg-black/50 px-1 py-0.5 rounded w-24 font-mono text-[11px]"
                          />
                        </div>
                      </td>

                      {/* Cell D: Expenses */}
                      <td className="px-4 py-2.5 border-r border-slate-800 text-right min-w-[140px]">
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-slate-500 text-[10px]">£</span>
                          <input
                            type="number"
                            value={row.expenses}
                            onChange={(e) => handleCellChange(row.id, "expenses", e.target.value)}
                            onFocus={() => setActiveCell({ rowId: row.id, col: "D" })}
                            onBlur={() => setActiveCell(null)}
                            className="bg-transparent border-0 text-right font-bold text-slate-300 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:bg-black/50 px-1 py-0.5 rounded w-24 font-mono text-[11px]"
                          />
                        </div>
                      </td>

                      {/* Cell E: Rolling turnover running total */}
                      <td className={`px-4 py-2.5 border-r border-slate-800 text-right font-mono font-black min-w-[150px] relative transition-all duration-200 ${
                        isBreached ? "bg-rose-950/20 text-rose-400" : "bg-slate-900/10 text-slate-200"
                      }`}
                        title={`Formula active: SUMIFS(C1:C${rowNum}, A1:A${rowNum}, ">=EDATE(A${rowNum},-12)")`}
                      >
                        <div className="flex items-center justify-end gap-1.5">
                          <span className="text-xs">£</span>
                          <span>{runningTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          {isBreached && (
                            <span className="text-[8px] bg-rose-500 text-white font-heavy px-1.5 py-0.5 rounded ml-1 animate-pulse shrink-0 tracking-widest font-mono">
                              BREACH
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Row delete action button */}
                      <td className="px-3 py-2.5 text-center">
                        <button
                          onClick={() => handleDeleteRow(row.id)}
                          className="p-1 hover:bg-slate-800 text-slate-500 hover:text-rose-400 rounded-md transition-colors cursor-pointer block mx-auto"
                          title="Purge spreadsheet row record"
                        >
                          <Trash2 size={13} colSpan={1} />
                        </button>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </tbody>

            {/* Structured Table Summary Row */}
            <tfoot className="bg-[#080d20] border-t border-slate-800 text-[11px] font-mono leading-none tracking-wider text-slate-400">
              <tr className="font-bold">
                <td className="bg-slate-900 border-r border-slate-800 py-3 text-center">Σ</td>
                <td className="px-4 py-3 border-r border-slate-800">TOTAL LEDGER SUMMARY</td>
                <td className="px-4 py-3 border-r border-slate-800 text-center">-</td>
                <td className="px-4 py-3 border-r border-slate-800 text-right text-emerald-400">
                  £{totalIncome.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
                <td className="px-4 py-3 border-r border-slate-800 text-right text-slate-300">
                  £{totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
                <td className="px-4 py-3 border-r border-slate-800 text-right text-cyan-400 bg-slate-950/40">
                  Margin: £{currentNetMargin.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
                <td className="px-3 py-3 text-center bg-transparent">-</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Row Generation Controller Box */}
      <div className="bg-[#080d20]/90 border border-slate-800 rounded-xl p-5 space-y-4">
        <h4 className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-widest flex items-center gap-1.5">
          <Plus size={14} /> INITIALIZE NEW LEDGER TRANSACTIONS (CELL APPEND HANDSHAKE)
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-[8px] font-mono text-slate-400 uppercase tracking-wider block font-bold mb-1.5">
              A) ISO date selection
            </label>
            <input
              type="date"
              value={newRow.date}
              onChange={(e) => setNewRow(p => ({ ...p, date: e.target.value }))}
              className="w-full h-9 px-3 bg-black border border-slate-800 text-slate-200 font-mono text-xs rounded-lg focus:outline-none focus:border-cyan-500 font-bold"
            />
          </div>

          <div>
            <label className="text-[8px] font-mono text-slate-400 uppercase tracking-wider block font-bold mb-1.5">
              B) Category allocation
            </label>
            <select
              value={newRow.category}
              onChange={(e) => setNewRow(p => ({ ...p, category: e.target.value as any }))}
              className="w-full h-9 px-3 bg-black border border-slate-800 text-slate-200 font-mono text-xs rounded-lg focus:outline-none focus:border-cyan-500 font-bold uppercase"
            >
              <option value="Hosting">Hosting</option>
              <option value="Ad">Ad</option>
              <option value="Domain">Domain</option>
              <option value="Service">Service</option>
            </select>
          </div>

          <div>
            <label className="text-[8px] font-mono text-slate-400 uppercase tracking-wider block font-bold mb-1.5">
              C) Income (Excl. VAT) (£)
            </label>
            <input
              type="number"
              value={newRow.income || ""}
              onChange={(e) => setNewRow(p => ({ ...p, income: Math.max(0, parseFloat(e.target.value) || 0) }))}
              placeholder="Excl. VAT amount..."
              className="w-full h-9 px-3 bg-black border border-slate-800 text-slate-100 font-mono text-xs rounded-lg focus:outline-none focus:border-cyan-500 font-bold"
            />
          </div>

          <div>
            <label className="text-[8px] font-mono text-slate-400 uppercase tracking-wider block font-bold mb-1.5">
              D) Expenses Outflow (£)
            </label>
            <input
              type="number"
              value={newRow.expenses || ""}
              onChange={(e) => setNewRow(p => ({ ...p, expenses: Math.max(0, parseFloat(e.target.value) || 0) }))}
              placeholder="Net Cost value..."
              className="w-full h-9 px-3 bg-black border border-slate-800 text-slate-100 font-mono text-xs rounded-lg focus:outline-none focus:border-cyan-500 font-bold"
            />
          </div>
        </div>

        <button
          onClick={handleAddRow}
          className="w-full py-2.5 bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-500 hover:to-cyan-600 border border-cyan-500/20 text-white font-mono text-xs font-bold uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          <span>COMMIT & COMPUTE NEW SPREADSHEET ROW</span>
        </button>
      </div>

      {/* Analytical VAT Indicator Guide Footer */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
        <div className="p-4 bg-[#0a0505]/40 border border-rose-500/10 rounded-xl space-y-2 text-left">
          <span className="text-[9px] font-mono text-rose-400 font-black tracking-widest block uppercase">
            ⚠️ MANDATORY REGISTRATION OBLIGATIONS (UK VAT ACT)
          </span>
          <p className="text-[8.5px] font-mono text-slate-400 leading-normal uppercase">
            If your rolling taxable turnover exceeds <strong className="text-rose-400">£90,000</strong> in any 12-month period, or you expect it to exceed that threshold in the next 30 days alone, you must alert HMRC within 30 days. Failure to notify triggers severe retro-levies and compounding penalties starting from the date of the breach.
          </p>
        </div>

        <div className="p-4 bg-[#050a06]/40 border border-emerald-500/10 rounded-xl space-y-2 text-left">
          <span className="text-[9px] font-mono text-emerald-400 font-black tracking-widest block uppercase">
            💡 NEXORAX ACCOUNTING ARCHITECT ADVANTAGES
          </span>
          <p className="text-[8.5px] font-mono text-slate-400 leading-normal uppercase">
            By excluding standard export transactions, B2B digital services delivered outside the UK/EU, and capital gains from the threshold sum, you can optimize your entity's tax profile. All B2B invoices generated globally by the NexoraX Neural Network track this ledger status.
          </p>
        </div>
      </div>
    </div>
  );
}
